import { useState } from 'react';

import { Box, Button, Card, Flex, Text, Tooltip } from '@mantine/core';

import { CreditSubscription, SubscriptionBillingCycle, SubscriptionData, SubscriptionType } from 'models';
import { IntlHelpers } from 'helpers/IntlHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { getSubscriptionMeta } from 'components/subscriptions/components/helpers/get-subscription-meta';

import { SubscriptionUpdateCommonProps } from '../../types';
import { CreditPackageSelector } from '../CreditPackageSelector';
import { PriceTagStacked } from '../PriceTag';
import { SubscribeModalContentContainer } from '../SubscribeModalContentContainer';
import { SubscribeModalContent } from '../SubscribeModalContents';
import { UsageGuidancePanel } from '../UsageGuidancePanel';
import { useSubscriptionUpdate } from '../useSubscriptionUpdate';

enum CreditPackageUpdateType {
    SIGN_UP,
    ADD_CREDITS,
    REMOVE_CREDITS,
    NO_CHANGE,
}

export const SubscribeForCredits = ({
    toSubscription,
    ...subscriptionProps
}: { toSubscription: CreditSubscription } & SubscriptionUpdateCommonProps) => {
    const { team } = subscriptionProps;

    const currentSubscription = SubscriptionHelpers.getSubscriptionData(SubscriptionType.CREDIT, team.subscriptions);

    const isBeingCancelled = SubscriptionHelpers.isGoingToBeCanceled(
        SubscriptionType.CREDIT,
        team.subscriptionUpdateNotice?.changes ?? [],
    );

    const [quantity, setQuantity] = useState(
        isBeingCancelled ? 0 : currentSubscription.quantity > 0 ? currentSubscription.quantity : null,
    );

    const updateType = getUpdateType(currentSubscription, quantity ?? 0, isBeingCancelled);

    const { submitSubscriptionUpdate, loading, ...updateStateAndController } = useSubscriptionUpdate({
        toSubscription,
        ...subscriptionProps,
        quantity: quantity ?? 0,
        isDowngrade: updateType === CreditPackageUpdateType.REMOVE_CREDITS,
    });

    const { billingCycle } = updateStateAndController;
    const currentQuantity = currentSubscription.quantity;

    const effectiveCurrentQuantity = isBeingCancelled ? 0 : currentSubscription.quantity;

    const buttonLabel = getButtonLabel(updateType, effectiveCurrentQuantity, quantity ?? 0, billingCycle);

    const modalTitle = getModalTitle(updateType);

    const { cancellationDate } = getSubscriptionMeta(team);

    return (
        <SubscribeModalContentContainer title={modalTitle}>
            <SubscribeModalContent
                type={SubscriptionType.CREDIT}
                toSubscription={toSubscription}
                {...subscriptionProps}
                {...updateStateAndController}
                quantity={quantity ?? 0}
                isDowngrade={updateType === CreditPackageUpdateType.REMOVE_CREDITS}
                SubmitButton={
                    <Tooltip
                        disabled={updateType !== CreditPackageUpdateType.NO_CHANGE}
                        label="This is your current monthly credit allocation."
                    >
                        <Button
                            fullWidth
                            size="lg"
                            onClick={submitSubscriptionUpdate}
                            loading={loading}
                            disabled={updateType === CreditPackageUpdateType.NO_CHANGE}
                        >
                            {buttonLabel}
                        </Button>
                    </Tooltip>
                }
            >
                <Box>
                    {currentSubscription.quantity > 0 && (
                        <>
                            <Text fw={600} fz={10} c="dimmed" tt="uppercase">
                                Current Credits
                            </Text>{' '}
                            <Card withBorder padding="lg" radius="md" mt={8} mb="lg">
                                <Flex justify="space-between" align="center">
                                    <Box>
                                        <Text fw={600}>{currentSubscription.quantity} credits</Text>
                                        <Text c="dimmed" size="sm">
                                            {currentQuantity} credits
                                            {billingCycle === SubscriptionBillingCycle.YEARLY
                                                ? ` × 12 months = ${IntlHelpers.formatNumber(currentQuantity * 12)} `
                                                : ''}
                                        </Text>
                                        {isBeingCancelled && (
                                            <Text c="red" mt={4}>
                                                Scheduled for cancellation
                                                {cancellationDate(SubscriptionType.CREDIT)
                                                    ? ` on ${cancellationDate(SubscriptionType.CREDIT)}`
                                                    : ''}
                                            </Text>
                                        )}
                                    </Box>
                                    <PriceTagStacked
                                        subscription={CreditSubscription.STANDARD}
                                        billingCycle={billingCycle}
                                        multiplier={currentQuantity}
                                        alwaysShow
                                    />
                                </Flex>
                            </Card>
                        </>
                    )}

                    <UsageGuidancePanel onSelectPreset={(amount) => setQuantity(amount)} />

                    <Text fw={600} fz={10} c="dimmed" tt="uppercase">
                        Adjust Quantity
                    </Text>

                    <CreditPackageSelector
                        value={quantity}
                        onChange={setQuantity}
                        showNone
                        billingCycle={updateStateAndController.billingCycle}
                        PriceTag={
                            <>
                                {quantity ? (
                                    <PriceTagStacked
                                        subscription={CreditSubscription.STANDARD}
                                        billingCycle={billingCycle}
                                        multiplier={quantity}
                                    />
                                ) : (
                                    <Box ta="right">
                                        <Text size="lg" fw={700}>
                                            —
                                        </Text>
                                    </Box>
                                )}
                            </>
                        }
                    />
                </Box>
            </SubscribeModalContent>
        </SubscribeModalContentContainer>
    );
};

const getUpdateType = (
    currentSubscription: SubscriptionData,
    quantity: number,
    isBeingCancelled: boolean,
): CreditPackageUpdateType => {
    if (currentSubscription.subscription === CreditSubscription.NONE) {
        return CreditPackageUpdateType.SIGN_UP;
    }

    const currentQuantity = isBeingCancelled ? 0 : currentSubscription.quantity;

    if (quantity === currentQuantity) {
        return CreditPackageUpdateType.NO_CHANGE;
    }

    if (quantity > currentQuantity) {
        return CreditPackageUpdateType.ADD_CREDITS;
    }

    // (quantity < currentQuantity)
    return CreditPackageUpdateType.REMOVE_CREDITS;
};

const getButtonLabel = (
    updateType: CreditPackageUpdateType,
    oldQuantity: number,
    newQuantity: number,
    billingCycle: SubscriptionBillingCycle,
) => {
    const multiplier = billingCycle === SubscriptionBillingCycle.YEARLY ? 12 : 1;

    switch (updateType) {
        case CreditPackageUpdateType.SIGN_UP: {
            return `Add ${newQuantity * multiplier} credits`;
        }
        case CreditPackageUpdateType.ADD_CREDITS: {
            return `Add ${(newQuantity - oldQuantity) * multiplier} credits`;
        }
        case CreditPackageUpdateType.REMOVE_CREDITS: {
            return `Remove ${(oldQuantity - newQuantity) * multiplier} credits`;
        }
        case CreditPackageUpdateType.NO_CHANGE: {
            return 'No changes yet';
        }
    }
};

const getModalTitle = (updateType: CreditPackageUpdateType) => {
    switch (updateType) {
        case CreditPackageUpdateType.SIGN_UP:
            return `Add Credits to Your Account`;

        case CreditPackageUpdateType.ADD_CREDITS:
            return `Add Credits to Your Subscription`;

        case CreditPackageUpdateType.REMOVE_CREDITS:
            return `Remove Credits from Your Subscription`;

        case CreditPackageUpdateType.NO_CHANGE:
            return `Edit Your Credits Subscription`;
    }
};
